// src/lib/FetchClient.ts
/**
 * A custom error class to provide more detailed information about fetch-related errors.
 * It includes the HTTP status and the body of the error response.
 */
export class FetchError extends Error {
  status: number;
  info: any;

  constructor(message: string, status: number, info: any) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.info = info;
  }
}

/**
 * A robust, client-side fetch wrapper with improved error handling and type safety.
 * It now intelligently handles Content-Type for file uploads.
 */
export const FetchClient = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const defaultHeaders: HeadersInit = {};

  // --- Start of Fix ---
  // Do not set Content-Type if the body is FormData.
  // The browser will automatically set it to multipart/form-data with the correct boundary.
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }
  // --- End of Fix ---

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include', // Add this line to ensure cookies are sent
  });

  if (!response.ok) {
    let errorInfo;
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
        try {
            errorInfo = await response.json();
        } catch (e) {
            errorInfo = { message: "JSON 오류 응답을 구문 분석하는 데 실패했습니다." };
        }
    } else {
        errorInfo = await response.text();
    }
    
    throw new FetchError(
      `Request failed with status ${response.status}`,
      response.status,
      errorInfo
    );
  }

  const successContentType = response.headers.get("content-type");
  if (response.status === 204 || !successContentType || !successContentType.includes("application/json")) {
    return undefined as T; 
  }

  return response.json() as Promise<T>;
};